import { useMemo } from 'react';
import { useFetchCompanyDetailQuery } from '../services/company.api';
import { useFetchFeePackageQuery } from '../services/feePackages.api';
import { Company } from '../stores/types';
import { PackageType } from '../stores/types/feePackage.interface';
import { useAuth } from './useAuth';
import { useCompany } from './useCompany';

export const useCompanyFeePackage = () => {
  const auth = useAuth();
  const { userScope } = useCompany();

  const { company, isCompanyLoading } = useFetchCompanyDetailQuery(
    {
      id: auth.user?.attributes?.profile?.lastActiveCompanyId,
      scope: userScope!,
    },
    {
      selectFromResult: (endPoint) => ({
        company: endPoint.data ? endPoint.data : ({} as Company),
        isCompanyLoading: endPoint.isLoading || endPoint.isFetching,
      }),
    },
  );

  const { feePackage } = useFetchFeePackageQuery(
    { flexPlanId: company?.feePackageId || '' },
    {
      skip: !company?.feePackageId, // Only run the query if company and feePackageId exists
      selectFromResult: (endPoint) => ({
        feePackage: endPoint.data,
      }),
    },
  );

  // Calculate derived values
  const isFlexPlan = useMemo(() => {
    return feePackage?.type === PackageType.FLEX_PLAN;
  }, [feePackage]);

  const isNonePlan = useMemo(() => {
    if (feePackage?.type === PackageType.NONE || !company?.feePackageId) {
      return true;
    }
  }, [feePackage, company?.feePackageId]);

  const sessionFee = useMemo(() => {
    const value = Number(company?.driverSessionFee);

    return Number.isFinite(value) ? value : 0;
  }, [company?.driverSessionFee]);

  return {
    company,
    feePackage,
    isFlexPlan,
    isNonePlan,
    sessionFee,
    isCompanyLoading,
  };
};
